import { Component, OnInit } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { forkJoin } from 'rxjs';
import { first } from 'rxjs/operators';
import { ITimeEntryCategory } from '../models/time-entry-category';
import { TimeEntryCategoryService } from '../services/time-entry-category.service';
import { sortAscSmallIcon, sortDescSmallIcon } from '@progress/kendo-svg-icons';
import { NameValidators } from 'src/app/shared/validators/name.validators';

@Component({
    selector: 'app-time-entry-category',
    templateUrl: './time-entry-category.component.html',
    styleUrls: ['./time-entry-category.component.css'],
    standalone: false
})
export class TimeEntryCategoryComponent implements OnInit {

  constructor(
    private route : ActivatedRoute,
    private timeEntryCategoryService: TimeEntryCategoryService
  ) { }
    
  timeEntryCategories: ITimeEntryCategory[] = [];
  public formGroup!: UntypedFormGroup;
  private editedRowIndex!: number;

    //icons
  public icons  = {
    sortAsc: sortAscSmallIcon,
    sortDesc: sortDescSmallIcon
  };

  ngOnInit(): void {
    this.timeEntryCategories = this.route.snapshot.data.timeEntryCategories as ITimeEntryCategory[];
  }

  updateList(){
    this.timeEntryCategoryService.searchTimeEntryCategories({pageNumber: 0, pageSize: 999999})
    .pipe(first())
    .subscribe(response => {
      this.timeEntryCategories = response.data;
    })
  }

  addHandler({ sender }){
    this.closeEditor(sender);

    this.formGroup = new UntypedFormGroup({
      name: new UntypedFormControl(null, NameValidators(() => this.timeEntryCategories.map(c => c.name), 75, true)),
      isProductive: new UntypedFormControl(false),
      color: new UntypedFormControl(null, Validators.required),
    });

    sender.addRow(this.formGroup);
  }

  editHandler({ sender, rowIndex, dataItem }){
    this.closeEditor(sender);

    this.formGroup = new UntypedFormGroup({
      name: new UntypedFormControl(dataItem.name,
        NameValidators(() => this.timeEntryCategories.filter(c => c.id !== dataItem.id).map(c => c.name), 75, true)),
      isProductive: new UntypedFormControl(dataItem.isProductive),
      color: new UntypedFormControl(dataItem.color, Validators.required),
    });

    this.editedRowIndex = rowIndex;

    sender.editRow(rowIndex, this.formGroup);
  }

  public cancelHandler({ sender, rowIndex }) {
    this.closeEditor(sender, rowIndex);
  }

  public saveHandler(event) {
    const id = event.dataItem.id ?? null;
    const request: any = {
      name: event.formGroup.value.name?.trim(),
      isProductive: event.formGroup.value.isProductive,
      color: event.formGroup.value.color,
      id,
    }
    if (!id) {
      // New categories go to the end; without this the backend stores sortOrder 0 for every new one.
      request.sortOrder = this.timeEntryCategories.length;
    }

    const save$ = id
      ? this.timeEntryCategoryService.updateTimeEntryCategory(request)
      : this.timeEntryCategoryService.createTimeEntryCategory(request);

    // Close the row only on success, so the input survives a rejected save (errors are shown by the interceptor).
    save$.subscribe(response => {
      event.sender.closeRow(event.rowIndex);
      this.updateList();
    });
  }

  private closeEditor(grid, rowIndex = this.editedRowIndex) {
    grid.closeRow(rowIndex);
    this.editedRowIndex = undefined;
    this.formGroup = undefined;
  }

  deletionDialogOpened = false;
  private itemToDelete: { id: string } | null = null;

  public removeHandler({ dataItem }) {
    this.itemToDelete = dataItem;
    this.deletionDialogOpened = true;
  }

  public deleteConfirmed() {
    const dataItem = this.itemToDelete;
    this.itemToDelete = null;
    if (!dataItem) return;
    this.timeEntryCategoryService.deleteTimeEntryCategory(dataItem.id)
    .subscribe(response => {
      this.updateList();
    });;
  }

  public onMoveUp(dataItem, index){
    this.move(dataItem, +1);
  }

  public onMoveDown(dataItem, index){
    this.move(dataItem, -1);
  }

  // Swap sortOrder with the neighbour at the target position so the order stays free of duplicates.
  private move(dataItem: ITimeEntryCategory, delta: number) {
    const target = dataItem.sortOrder + delta;
    const neighbour = this.timeEntryCategories.find(c => c.id !== dataItem.id && c.sortOrder === target);

    const updates = [
      this.timeEntryCategoryService.updateTimeEntryCategory({ ...dataItem, sortOrder: target }),
    ];
    if (neighbour) {
      updates.push(this.timeEntryCategoryService.updateTimeEntryCategory({ ...neighbour, sortOrder: dataItem.sortOrder }));
    }

    forkJoin(updates).subscribe(() => this.updateList());
  }

}
